import { Building2, CloudOff, Info, ShieldCheck, Stethoscope, Unlink } from 'lucide-react';
import { getGroupColor } from '@/lib/queues';
import { cn } from '@/lib/utils';

// Small chips shared by the Calendars list and editor. Colour lives on `bg-*`/`text-*` plus an
// inset ring: the app's unlayered `* { @apply border-border }` makes every `border-<colour>`
// utility dead, so a coloured edge must be a ring or an inset shadow, never a border.

const CHIP = 'inline-flex shrink-0 items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-bold leading-4';

/** Cyan "Plato" chip — the calendar was converted from Plato's appointment calendars. */
export function PlatoOriginBadge({ className, label = 'Plato', title = 'Synced from Plato' }) {
  return (
    <span
      title={title}
      className={cn(
        CHIP,
        'bg-cyan-50 text-cyan-800 ring-1 ring-inset ring-cyan-300/70 dark:bg-cyan-950/60 dark:text-cyan-200 dark:ring-cyan-500/40',
        className,
      )}
    >
      <ShieldCheck className="h-3 w-3" aria-hidden />
      {label}
    </span>
  );
}

/** Violet "Local only" chip — appointments never reach the Plato EMR. */
export function LocalOnlyBadge({ className }) {
  return (
    <span
      title="Never pushed to the Plato EMR"
      className={cn(
        CHIP,
        'bg-violet-50 text-violet-800 ring-1 ring-inset ring-violet-300/70 dark:bg-violet-950/60 dark:text-violet-200 dark:ring-violet-500/40',
        className,
      )}
    >
      <CloudOff className="h-3 w-3" aria-hidden />
      Local only
    </span>
  );
}

/** Sky "Non-blocking" chip — independent of the doctor's other calendars. */
export function NonBlockingBadge({ className }) {
  return (
    <span
      title="Independent of the doctor's other calendars"
      className={cn(
        CHIP,
        'bg-sky-50 text-sky-800 ring-1 ring-inset ring-sky-300/70 dark:bg-sky-950/60 dark:text-sky-200 dark:ring-sky-500/40',
        className,
      )}
    >
      <Unlink className="h-3 w-3" aria-hidden />
      Non-blocking
    </span>
  );
}

/** Active / Inactive status chip (same look as the queue cards). */
export function CalendarStatusChip({ active, className }) {
  if (active) {
    return (
      <span className={cn(CHIP, 'px-2', getGroupColor('emerald').chipClassName, className)}>
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-hidden />
        Active
      </span>
    );
  }
  return (
    <span className={cn(CHIP, 'px-2 text-muted-foreground shadow-[inset_0_0_0_1px_var(--border)]', className)}>
      Inactive
    </span>
  );
}

/**
 * Owner chip: `kind` is "doctor" | "clinic" | "standalone" (see calendarKind). Doctor and clinic
 * chips look like the queue list's "attached" chip; standalone reuses its amber "Unattached"
 * style because it means the same thing — nothing owns this calendar.
 */
export function CalendarKindChip({ kind, label, className }) {
  if (kind === 'standalone') {
    return (
      <span className={cn(CHIP, 'px-2 py-1 text-[12px]', getGroupColor('amber').chipClassName, className)}>
        <Info className="h-3.5 w-3.5" aria-hidden />
        {label || 'Standalone'}
      </span>
    );
  }
  const Icon = kind === 'clinic' ? Building2 : Stethoscope;
  return (
    <span className={cn(CHIP, 'bg-accent px-2 py-1 text-[12px] text-accent-foreground', className)}>
      <Icon className="h-3.5 w-3.5" aria-hidden />
      <span className="truncate">{label || (kind === 'clinic' ? 'Clinic' : 'Doctor')}</span>
    </span>
  );
}
